/* eslint-disable @typescript-eslint/ban-types */
import React, {
  ComponentClass,
  ComponentType,
  ReactNode,
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
} from "react";
import ModalContent from "../components/Modal/MainModalContent";

interface ModalContextUIState {
  modalComponent: ComponentType<any> | null;
  props?: any;
  visible: boolean;
}

interface ModalContextValue {
  showModal: (htmlContent?: string) => void;
  hideModal: () => void;
  clearModals: () => void;
}

const MainModalContext = createContext<ModalContextValue>({
  clearModals: () => {},
  showModal: () => {},
  hideModal: () => {},
});

/**
 * React hook which gives functionality to trigger a modal from anywhere in the app
 * If more than one modal is triggered while another is open, it will be added to a queue.
 * ALWAYS clear queue onCTAClick, in order to avoid side effects.
 * @returns Modal Context Object containing showModal, hideModal, clearModals functions
 */
export const useModal = (): ModalContextValue => {
  const modalContext = useContext(MainModalContext);
  return modalContext;
};

/**
 * HOC to provide a component with the showModal and hideModal functions from the
 * modal context service, where converting a class component to a hook is not possible
 * or feasible.
 * @param Component - React Class component to be injected with the modal context
 */
export const withModal =
  <P extends {}>(Component: ComponentClass<P & ModalContextValue>): React.FC<P> =>
  (props) => {
    const { showModal, hideModal, clearModals } = useModal();
    return (
      <Component
        showModal={showModal}
        hideModal={hideModal}
        clearModals={clearModals}
        {...props}
      />
    );
  };

/**
 * Context Provider for the Modal Context service. Needed to use the withModal HOC
 * or the useModal hook. This enforces the idea that while you may have multiple modal
 * instances or layouts, only one modal may be present at any given time.
 */
export const MainModalContextProvider: React.FC<{ children?: ReactNode }> = ({
  children,
}) => {
  const [modalUIState, setModalUIState] = useState<ModalContextUIState>({
    modalComponent: null,
    props: null,
    visible: false,
  });

  const modalQueueRef = useRef<ModalContextUIState[]>([]);
  const modalQueue = modalQueueRef.current;

  /**
   * If htmlContent is provided, we'll send it down as a prop to the modal.
   * This allows you to call window.showModal("<p>some HTML</p>")
   */
  const showModal = (htmlContent?: string): void => {
    modalQueue.push({
      modalComponent: ModalContent,
      props: { htmlContent },
      visible: true,
    });

    setModalUIState((prevModal) => {
      if (!prevModal.visible) {
        return modalQueue.shift() as ModalContextUIState;
      }
      return prevModal;
    });
  };

  const hideModal = (): void => {
    if (modalQueue.length) {
      setModalUIState(modalQueue.shift() as ModalContextUIState);
    } else {
      setModalUIState({
        modalComponent: null,
        props: null,
        visible: false,
      });
    }
  };

  const clearModals = (): void => {
    modalQueue.length = 0;
    setModalUIState({
      modalComponent: null,
      props: null,
      visible: false,
    });
  };

  const contextValue = {
    clearModals,
    showModal,
    hideModal,
  };

  const { modalComponent: Component, props, visible } = modalUIState;

  useEffect(() => {
    (window as any).showModal = showModal;
    (window as any).hideModal = hideModal;
    (window as any).clearModals = clearModals;

    return () => {
      (window as any).showModal = undefined;
      (window as any).hideModal = undefined;
      (window as any).clearModals = undefined;
    };
  }, [showModal, hideModal, clearModals]);

  return (
    <MainModalContext.Provider value={contextValue}>
      {children}
      {visible && Component && (
        <Component onClose={hideModal} {...props} />
      )}
    </MainModalContext.Provider>
  );
};
